// Quota sets (design §4, decision 7) and creating many quotas at once (7h).
//
// A set is built from one to three dimensions, each a question with answer bands:
//   dimension  {questionId, bands: [{id, label, optionIds}], leftOut: [optionId]}
// Every combination of bands is a cell; each cell with a limit becomes one quota, named
// "<set name> - <band> x <band>", with set_name = the set name.
import { safeDecode } from '../utils/decode';

export const MAX_DIMENSIONS = 3;
export const SET_KINDS = new Set(['choice', 'multi-select']);
// Wrangler holds the survey's lock while it writes one row per complete (or place held) each
// new quota covers, so large creates go in batches of about this many rows (decided 20 October 2026).
export const MAX_ROWS_PER_BATCH = 50000;
export const MAX_QUOTAS_PER_BATCH = 5000;

let nextBand = 1;
const bandId = () => `b${nextBand++}`;

const optionTitle = (question, optionId) => {
  const option = question.options.find((o) => o.option_id === optionId);
  return option ? safeDecode(option.title) : String(optionId);
};

/** A question as a dimension: each answer its own band, none left out. */
export const newDimension = (question) => ({
  questionId: question.question_id,
  bands: question.options.map((o) => ({ id: bandId(), label: safeDecode(o.title), optionIds: [o.option_id] })),
  leftOut: [],
});

/** The chosen bands merged into one, where the first of them was: "18-24 + 25-34". */
export const mergeBands = (dimension, ids) => {
  const chosen = dimension.bands.filter((b) => ids.includes(b.id));
  if (chosen.length < 2) return dimension;
  const merged = {
    id: bandId(), label: chosen.map((b) => b.label).join(' + '), optionIds: chosen.flatMap((b) => b.optionIds),
  };
  const bands = [];
  for (const band of dimension.bands) {
    if (band.id === chosen[0].id) bands.push(merged);
    else if (!ids.includes(band.id)) bands.push(band);
  }
  return { ...dimension, bands };
};

/** A merged band back to one band per answer, in its place. */
export const splitBand = (dimension, id, question) => ({
  ...dimension,
  bands: dimension.bands.flatMap((band) => (band.id !== id ? [band]
    : band.optionIds.map((optionId) => ({ id: bandId(), label: optionTitle(question, optionId), optionIds: [optionId] })))),
});

/** An answer out of its band (and the band gone if it was its last answer). */
export const leaveOut = (dimension, optionId) => ({
  ...dimension,
  bands: dimension.bands.map((b) => ({ ...b, optionIds: b.optionIds.filter((id) => id !== optionId) }))
    .filter((b) => b.optionIds.length > 0),
  leftOut: [...dimension.leftOut, optionId],
});

/** A left-out answer back, as a band of its own at the end. */
export const addBack = (dimension, optionId, question) => ({
  ...dimension,
  bands: [...dimension.bands, { id: bandId(), label: optionTitle(question, optionId), optionIds: [optionId] }],
  leftOut: dimension.leftOut.filter((id) => id !== optionId),
});

export const renameBand = (dimension, id, label) => ({
  ...dimension, bands: dimension.bands.map((b) => (b.id === id ? { ...b, label } : b)),
});

/** Problems with the dimensions, as messages. */
export const dimensionProblems = (dimensions) => {
  const problems = [];
  if (!dimensions.length) problems.push('Choose at least one question.');
  dimensions.forEach((d, i) => {
    if (!d.bands.length) problems.push(`Question ${i + 1}: keep at least one answer.`);
    const labels = d.bands.map((b) => b.label.trim());
    if (labels.some((l) => !l)) problems.push(`Question ${i + 1}: every band needs a name.`);
    if (new Set(labels).size !== labels.length) problems.push(`Question ${i + 1}: two bands have the same name.`);
  });
  const ids = dimensions.map((d) => d.questionId);
  if (new Set(ids).size !== ids.length) problems.push('The same question is chosen twice.');
  return problems;
};

/** Every combination of bands, the first dimension outermost: [{key, bands}]. */
export const setCells = (dimensions) => dimensions.reduce(
  (cells, dimension) => cells.flatMap((cell) => dimension.bands.map((band) => ({
    key: cell.key ? `${cell.key}|${band.id}` : band.id, bands: [...cell.bands, band],
  }))),
  [{ key: '', bands: [] }],
).filter((cell) => cell.bands.length === dimensions.length && dimensions.length > 0);

export const cellName = (setName, bands) => `${setName.trim()} - ${bands.map((b) => b.label.trim()).join(' x ')}`;

/** The cell's logic: "is one of" its band's answers, for every dimension (AND). */
export const cellLogic = (dimensions, bands) => {
  const conditions = dimensions.map((d, i) => ({ question_id: d.questionId, operator: 'in', values: bands[i].optionIds }));
  return conditions.length === 1 ? conditions[0] : { op: 'and', children: conditions };
};

/**
 * Whole numbers in proportion to `weights` that add up to `total` exactly (largest remainder):
 * everyone gets the whole part of their share, then the leftover units go to the largest
 * fractional parts, earlier first on a tie.
 */
export const largestRemainder = (total, weights) => {
  const sum = weights.reduce((s, w) => s + w, 0);
  if (!(sum > 0) || !(total > 0)) return weights.map(() => 0);
  const exact = weights.map((w) => (total * w) / sum);
  const out = exact.map(Math.floor);
  let left = total - out.reduce((s, n) => s + n, 0);
  const order = exact.map((e, i) => [e - Math.floor(e), i]).sort((a, b) => b[0] - a[0] || a[1] - b[1]);
  for (const [, i] of order) {
    if (left <= 0) break;
    out[i] += 1;
    left -= 1;
  }
  return out;
};

/** Percentages per band (strings, by band id) that don't add up to 100, by dimension index. */
export const percentProblems = (dimensions, percents) => dimensions.map((d, i) => {
  const values = d.bands.map((b) => percents[b.id]);
  if (values.some((v) => v === undefined || v === '' || !(Number(v) >= 0))) return `Question ${i + 1}: give every band a percentage.`;
  const sum = values.reduce((s, v) => s + Number(v), 0);
  return Math.abs(sum - 100) > 0.01 ? `Question ${i + 1}: the percentages add up to ${Math.round(sum * 100) / 100}, not 100.` : null;
}).filter(Boolean);

/**
 * Cell limits from a total and band percentages, assuming the dimensions are independent:
 * each cell's share is the product of its bands' percentages. {cellKey: limit}.
 */
export const calculateLimits = (dimensions, total, percents) => {
  const cells = setCells(dimensions);
  const weights = cells.map((cell) => cell.bands.reduce((w, b) => w * (Number(percents[b.id]) / 100), 1));
  const limits = largestRemainder(total, weights);
  return Object.fromEntries(cells.map((cell, i) => [cell.key, limits[i]]));
};

/** Text pasted from Excel as rows of cells (tab-separated, one line per row). */
export const parsePaste = (text) => {
  const lines = text.replace(/\r\n?/g, '\n').split('\n');
  if (lines.length > 1 && lines[lines.length - 1] === '') lines.pop();
  return lines.map((line) => line.split('\t').map((v) => v.trim()));
};

/**
 * Development only: a smaller batch size from localStorage ("wrangler-dev-batch-rows"), so real
 * batching can be tried against a local Wrangler with few completes. Production builds and tests
 * never read it.
 */
const devBatchRows = () => {
  if (process.env.NODE_ENV !== 'development') return null;
  try {
    const rows = Number(window.localStorage.getItem('wrangler-dev-batch-rows'));
    return rows > 0 ? rows : null;
  } catch {
    return null;
  }
};

/**
 * Batches for creating `count` quotas, as [start, end) ranges in order: at most `maxQuotas` each,
 * and at most about `maxRows` rows written each, by the preview's counts (one row per complete or
 * place held, plus the quota itself). A quota bigger than `maxRows` on its own goes alone.
 */
export const planBatches = (results, { maxRows = devBatchRows() ?? MAX_ROWS_PER_BATCH, maxQuotas = MAX_QUOTAS_PER_BATCH } = {}) => {
  const batches = [];
  let start = 0;
  let rows = 0;
  results.forEach((r, i) => {
    const size = 1 + (r?.complete_count || 0) + (r?.reserved_count || 0);
    if (i > start && (rows + size > maxRows || i - start >= maxQuotas)) {
      batches.push([start, i]);
      start = i;
      rows = 0;
    }
    rows += size;
  });
  if (results.length > start) batches.push([start, results.length]);
  return batches;
};
